// ROUM-VENTUREGEN-SCANBTN-20260620
import { officeOf, officeInfo } from './types';
import React from 'react';
import { masterMissing } from './master';
import { saveFilesToFolder } from './docgen';
import { s3upload, fetchFileBlob } from './api';
import { makeScannedPdf, scanAndSaveToFolder } from './scanpdf';
import { projectDir, projectDirName, changeProjectDir, onFolderChange, folderSupported } from './projectFolder';
import type { Project, StoreState, StoreKey, S3State, Master } from './types';

export function getS3(p: Project): S3State {
  return (p.s3 as S3State | undefined) || { vics: { id: '', pw: '' }, up: {} };
}
export function mutS3(p: Project, fn: (s: S3State) => void): Partial<Project> {
  const st = JSON.parse(JSON.stringify(getS3(p))) as S3State;
  st.vics = st.vics || { id: '', pw: '' };
  st.up = st.up || {};
  fn(st);
  return { s3: st };
}

export function MasterChip({ m, s4 }: { m?: Master; s4?: boolean }) {
  if (!m) return null;
  const miss = masterMissing(m, { s4 });
  // 무엇이 비었는지 첫 칸 이름을 보여 줌 · 전체는 마우스를 올리면(2026-10-23)
  return miss.length
    ? <span className="pill p-amber" title={'비어 있는 칸: ' + miss.join(', ')}
        style={{ marginLeft: 6, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
        {miss[0]}{miss.length > 1 ? ` 외 ${miss.length - 1}칸` : ''} 비어 있음
      </span>
    : <span className="pill p-green" style={{ marginLeft: 6 }}>모든 칸 입력 완료</span>;
}

export function MasterCollapse({ open, onToggle, title, sub, chip, children }: {
  open: boolean; onToggle: () => void; title: string; sub: string; chip?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="mpanel">
      <div className="mpanel-h" onClick={onToggle}>
        <div><b>{title}</b>{chip}<span>{sub}</span></div>
        <span className="mchev">{open ? '▴' : '▾'}</span>
      </div>
      {open ? children : null}
    </div>
  );
}

export function getStore(p: Project, s: StoreKey): StoreState {
  return (p[s] as StoreState | undefined) || { up: {} };
}
export function getUp(p: Project, s: StoreKey, key: string): string {
  const st = getStore(p, s);
  return (st.up && st.up[key]) || '';
}
export function getUpKey(p: Project, s: StoreKey, key: string): string {
  const st = getStore(p, s);
  return (st.upKey && st.upKey[key]) || '';
}
export function getTxt(p: Project, s: StoreKey, key: string): string {
  const st = getStore(p, s);
  return (st.txt && st.txt[key]) || '';
}
export function getChk(p: Project, s: StoreKey, key: string): boolean {
  const st = getStore(p, s);
  return !!(st.chk && st.chk[key]);
}
export function mutStore(p: Project, s: StoreKey, fn: (st: StoreState) => void): Partial<Project> {
  const st = JSON.parse(JSON.stringify(getStore(p, s))) as StoreState;
  st.up = st.up || {};
  fn(st);
  return { [s]: st } as Partial<Project>;
}

/** 업로드 파일명을 "제목_조합명.확장자"로 변경 (파일명 금지문자 제거) */
export function renameUpload(f: File, docName: string, org: string): File {
  const m = f.name.match(/\.[A-Za-z0-9]{1,5}$/);
  const ext = m ? m[0] : '';
  const clean = (s: string): string => (s || '').replace(/[/\\?%*:|"<>]/g, '').replace(/\s+/g, ' ').trim();
  const base = (clean(docName) || '파일') + '_' + (clean(org) || '조합');
  return new File([f], base + ext, { type: f.type });
}

/** 표시·다운로드용 정식 파일명 — 저장된 이름의 확장자를 살려 "제목_조합명.확장자"로 산출 (리네임 전 업로드분도 일관 표시/다운로드) */
export function dispName(storedName: string, docName: string, org: string): string {
  if (!storedName) return storedName;
  const m = storedName.match(/\.[A-Za-z0-9]{1,5}$/);
  const ext = m ? m[0] : '';
  const clean = (s: string): string => (s || '').replace(/[/\\?%*:|"<>]/g, '').replace(/\s+/g, ' ').trim();
  return (clean(docName) || '파일') + '_' + (clean(org) || '조합') + ext;
}

/** 파일을 실제 S3에 업로드하고 파일명+S3키를 store에 저장하는 onUp 핸들러 생성 → 미리보기·다운로드 가능. docName 지정 시 "제목_조합명"으로 리네임 */
export function s3UploadHandler(p: Project, s: StoreKey, key: string, onPatch: (x: Partial<Project>) => void, toast: (m: string) => void, docName?: string) {
  return async (f0: File): Promise<void> => {
    try {
      const f = docName ? renameUpload(f0, docName, p.org || '') : f0;
      const r = await s3upload(p.id, f);
      onPatch(mutStore(p, s, (st) => { st.up[key] = r.name; st.upKey = st.upKey || {}; st.upKey[key] = r.key; }));
      toast('업로드됨 · ' + r.name);
    } catch (e) { toast((e as Error).message); }
  };
}

function onPickFile(e: React.ChangeEvent<HTMLInputElement>, cb: (f: File) => void) {
  const f = e.target.files && e.target.files[0];
  if (f) cb(f);
  e.target.value = '';
}

export function OkFile({ name, pill, reLabel, onPv, onDl, onRe }: { name: string; pill?: string; reLabel?: string; onPv?: () => void; onDl?: () => void; onRe: (f: File) => void }) {
  return (
    <div className="s3file ok"><span className="rf">📄 {name}</span><span className="pill p-green">{pill || '업로드됨'}</span><span className="grow" />
      {onPv ? <button className="rbtn" onClick={onPv}>👁 미리보기</button> : null}
      {onDl ? <button className="rbtn" onClick={onDl}>⬇ 다운로드</button> : null}
      <label className="upbtn sm">{reLabel || '다시 업로드'}<input type="file" hidden onChange={(e) => onPickFile(e, onRe)} /></label>
    </div>
  );
}
export function UpBtn({ label, small, arrow, onUp }: { label: string; small?: boolean; arrow?: boolean; onUp: (f: File) => void }) {
  const [drag, setDrag] = React.useState(false);
  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) onUp(f);
  };
  const dragStyle: React.CSSProperties | undefined = drag
    ? { outline: '2px dashed #6b8afd', outlineOffset: 2, background: 'rgba(107,138,253,0.10)' }
    : undefined;
  return (
    <label
      className={'upbtn' + (small ? ' sm' : '')}
      style={dragStyle}
      onDragOver={(e) => { e.preventDefault(); if (!drag) setDrag(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
      onDrop={onDrop}
    >{arrow === false ? '' : '⬆ '}{drag ? '여기에 놓기' : label}<input type="file" hidden onChange={(e) => onPickFile(e, onUp)} /></label>
  );
}
// 클릭 + 드래그앤드롭 모두 지원하는 업로드 라벨 (text는 호출측에서 busy 상태 반영해 전달)
export function UpLabel({ text, disabled, warn, onUp }: { text: string; disabled?: boolean; warn?: boolean; onUp: (f: File) => void }) {
  const [drag, setDrag] = React.useState(false);
  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDrag(false);
    if (disabled) return;
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) onUp(f);
  };
  const dragStyle: React.CSSProperties | undefined = drag && !disabled
    ? { outline: '2px dashed #6b8afd', outlineOffset: 2, background: 'rgba(107,138,253,0.10)' }
    : undefined;
  return (
    <label
      className={'upbtn sm' + (warn ? ' warn' : '')}
      style={dragStyle}
      onDragOver={(e) => { e.preventDefault(); if (!disabled && !drag) setDrag(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
      onDrop={onDrop}
    >{drag && !disabled ? '여기에 놓기' : text}<input type="file" hidden disabled={disabled} onChange={(e) => onPickFile(e, onUp)} /></label>
  );
}
export function RefFile({ name, tag, onPv, onDl }: { name: string; tag: string; onPv: () => void; onDl?: () => void }) {
  return (
    <div className="s3file"><span className="rf">📄 {name}</span><span className="pill p-blue">{tag}</span><span className="grow" />
      <button className="rbtn" onClick={onPv}>👁 미리보기</button>
      {onDl ? <button className="rbtn" onClick={onDl}>⬇ 다운로드</button> : null}
    </div>
  );
}
export function Wait({ msg }: { msg: string }) {
  return <div className="s3file"><span className="s3muted">{msg}</span></div>;
}
export function RefDoc({ pdf, tag, onPv, onDl, toast }: { pdf: string; tag?: string; onPv: () => void; onDl?: () => void; toast: (m: string) => void }) {
  return (
    <div className="s3file"><span className="rf">📕 {pdf}</span><span className="pill p-blue">{tag || '앞단계 자동 연동'}</span><span className="grow" />
      <button className="rbtn" onClick={onPv}>👁 미리보기</button>
      <button className="rbtn" onClick={onDl || onPv}>⬇ PDF</button>
    </div>
  );
}
/** link — 제목 바로 오른쪽 바깥 사이트 바로가기(예: 조합인감등록부 → 모두싸인 · 2026-11-01 지도사) */
export function S3Row({ no, title, note, opt, action, link, children }: { no: number | string; title: string; note?: string; opt?: boolean; action?: React.ReactNode; link?: { label: string; href: string }; children: React.ReactNode }) {
  return (
    <div className="s3row">
      <div className="s3h"><span className="cnum">{no}</span><b>{title}</b>{link ? <a className="pill p-blue" href={link.href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>{link.label} ↗</a> : null}{opt ? <span className="pill p-gray">선택</span> : null}{action ? <span className="grow" /> : null}{action}</div>
      {note ? <div className="s3note">{note}</div> : null}
      {children}
    </div>
  );
}

export function FormDl({ href, name }: { href: string; name: string }) {
  return <a className="cact formdl" href={href} download={name}>⬇ 양식 다운로드</a>;
}

/** 단계 우측 상단 일괄저장 버튼 — 이 단계 문서를 한 폴더에 저장(미지원 브라우저는 개별 다운로드) + 전체 스캔본 */
export function BulkSaveBtn({ files, toast, pid }: { files: { key: string; name: string; signed?: boolean; getBlob?: () => Promise<Blob> }[]; toast: (m: string) => void; pid?: string }) {
  const [busy, setBusy] = React.useState('');
  const n = files.filter((f) => f.key || f.getBlob).length;
  const go = async () => {
    if (busy || !n) return;
    setBusy('dl');
    try { await saveFilesToFolder(files, toast, pid); } finally { setBusy(''); }
  };
  // 🖨 전체 스캔본: 이 단계 PDF 전체를 스캔효과 적용해 폴더 저장(날인본은 도장·서명 포함). 폴더선택 먼저→변환(제스처 보존). 비PDF는 건너뜀
  const goScan = async () => {
    if (busy || !n) return;
    setBusy('scan');
    try {
      const targets = files.filter((f) => f.key || f.getBlob);
      const r = await scanAndSaveToFolder(
        targets,
        async (t: { key: string; name: string; signed?: boolean; getBlob?: () => Promise<Blob> }) => { const blob = t.getBlob ? await t.getBlob() : await fetchFileBlob(t.key, t.name); return t.signed ? makeScannedPdf(blob) : blob; },
        toast,
        pid ? () => projectDir(pid) : undefined,
      );
      if (r.cancelled) return;
      if (!r.ok) { toast('스캔할 PDF가 없습니다'); return; }
      toast('스캔본 ' + r.ok + '개 저장' + (r.fail ? ' · ' + r.fail + '개 건너뜀(비PDF)' : ''));
    } catch (e) { toast((e as Error).message); } finally { setBusy(''); }
  };
  return (
    <>
      {pid ? <FolderChip pid={pid} toast={toast} /> : null}
      <button
        className="upbtn sm"
        style={pid ? undefined : { marginLeft: 'auto' }}
        disabled={!!busy || !n}
        onClick={go}
        title="이 단계 문서를 일괄 다운로드(브라우저 다운로드 폴더)"
      >{busy === 'dl' ? '다운로드 중…' : '⬇ 전체 다운로드' + (n ? ' (' + n + ')' : '')}</button>
      <button
        className="upbtn sm"
        disabled={!!busy || !n}
        onClick={goScan}
        title="이 단계 PDF 전체에 스캔효과 적용 후 폴더 저장(날인본은 도장·서명 포함)"
      >{busy === 'scan' ? '스캔 중…' : '🖨 전체 스캔본' + (n ? ' (' + n + ')' : '')}</button>
    </>
  );
}

/** 조합 저장 폴더 표시 · 바꾸기 — 처음 저장할 때 고른 폴더를 이 조합의 모든 다운로드 · 스캔본이 같이 씀 (2026-11-01 지도사) */
export function FolderChip({ pid, toast }: { pid: string; toast: (m: string) => void }) {
  const [name, setName] = React.useState('');
  React.useEffect(() => {
    let alive = true;
    const load = () => { projectDirName(pid).then((n) => { if (alive) setName(n); }).catch(() => undefined); };
    load();
    const off = onFolderChange(load);
    return () => { alive = false; off(); };
  }, [pid]);
  if (!folderSupported()) return null;
  const change = async () => {
    try { const n = await changeProjectDir(pid); if (n) { setName(n); toast(`이 조합 파일은 이제 「${n}」 폴더에 저장합니다`); } }
    catch { /* 취소 */ }
  };
  return (
    <button type="button" className="upbtn sm" style={{ marginLeft: 'auto' }} onClick={() => void change()}
      title={name ? '이 조합의 원본 · 스캔본 · 전체 다운로드가 저장되는 폴더 — 눌러서 바꾸기' : '처음 저장할 때 폴더를 한 번 고르면 이 조합 파일은 모두 그 폴더에 저장됩니다 — 지금 고르기'}>
      📁 {name ? `${name} · 바꾸기` : '저장 폴더 고르기'}
    </button>
  );
}

/** 진행 주체 배지 — 로움 / 올댓벤처스 (목록 행·상세 헤더 공용, 2026-09-24) */
export function OfficePill({ p, long }: { p: { office?: unknown }; long?: boolean }) {
  const o = officeInfo(officeOf(p));
  return <span className={'pill office ' + o.v} title={'진행 주체 · ' + o.name}>{long ? o.name : o.short}</span>;
}
